import { Box, Button, CircularProgress, Typography } from "@mui/material";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import ReplayIcon from "@mui/icons-material/Replay";

interface IMVPCameraStreamProps {
	cameraId: number;
}

const MVPCameraStream: FC<IMVPCameraStreamProps> = ({ cameraId }) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const canvasWrapperRef = useRef<HTMLDivElement>(null);
	const playerRef = useRef<Window["HxPlayer"]>(null);

	const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });
	const [streamError, setStreamError] = useState<string>("");
	const [showLoader, setShowLoader] = useState<boolean>(true);

	function handleStreamError(error: string) {
		setShowLoader(false);
		setStreamError(error);
	}

	function handleStreamStart() {
		setStreamError("");
		setShowLoader(false);
	}

	function getWebsocketURL(camera: number) {
		const protocol = `ws${window.location.protocol.replace("http", "")}`;
		const host = window.location.hostname;
		const port = "";
		const endpoint = "/dws/video/socket_stream";

		let url = `${protocol}//${host}`;
		if (port) url += `:${port}`;
		url += `${endpoint}/${camera}/`;

		return url;
	}

	const connectWebsocket = useCallback(() => {
		if (canvasRef.current && cameraId) {
			setShowLoader(true);

			if (playerRef.current) {
				playerRef.current.stopvideo();
				playerRef.current = null;
			}

			const websocketURL = getWebsocketURL(cameraId);

			playerRef.current = new window.HxPlayer();
			playerRef.current.init({ canvas: canvasRef.current, width: 640, height: 352 });
			playerRef.current.playvideo(websocketURL, handleStreamError, handleStreamStart);
		}
	}, [cameraId, canvasRef]);

	function handleRetryButtonClick() {
		setStreamError("");
		connectWebsocket();
	}

	useEffect(() => {
		if (canvasWrapperRef.current) {
			const { offsetWidth: canvasWidth, offsetHeight: canvasHeight } = canvasWrapperRef.current;

			if (canvasWidth && canvasHeight) {
				let width = canvasWidth;
				let height = canvasHeight;

				if (width / height > 16 / 9) {
					height = canvasHeight;
					width = (height * 16) / 9;
				} else {
					width = canvasWidth;
					height = (width * 9) / 16;
				}

				setCanvasDimensions({ width, height });
			}
		}
	}, [canvasWrapperRef.current?.offsetWidth, canvasWrapperRef.current?.offsetHeight]);

	useEffect(() => {
		connectWebsocket();

		return () => {
			if (playerRef.current) {
				playerRef.current.stopvideo();
				playerRef.current = null;
			}
		};
	}, [connectWebsocket]);

	return (
		<Box className="stream-canvas-wrapper" ref={canvasWrapperRef}>
			<canvas id="stream-canvas" width={canvasDimensions.width} height={canvasDimensions.height} ref={canvasRef} />

			{streamError ? (
				<Box className="stream-canvas-overlay">
					<Typography>{streamError}</Typography>

					<Button
						disableElevation
						variant="contained"
						color="primary"
						startIcon={<ReplayIcon />}
						onClick={handleRetryButtonClick}
					>
						Retry
					</Button>
				</Box>
			) : null}

			{showLoader ? (
				<Box className="stream-canvas-overlay">
					<CircularProgress />
				</Box>
			) : null}
		</Box>
	);
};

export default MVPCameraStream;
